import "./featuredInfo.css";
import {
  ArrowDownward,
  ArrowUpward,
  AccountBalanceWalletOutlined,
  ShoppingBagOutlined,
  PeopleAltOutlined,
  Inventory2Outlined,
} from "@mui/icons-material";
import { useGetAllOrderQuery } from "../../../services/orderApi";
import { useGetAllCustomersQuery } from "../../../services/userCRUDApi";
import { useGetLowStockVariantsQuery } from "../../../services/productApi";
import { AdminCard, AdminSkeleton } from "../../../components/ui";

const monthKey = (dateStr) => dateStr?.slice(0, 7); // "YYYY-MM"

const sumRevenue = (orders) => orders.reduce((total, o) => total + Number(o.total_Amount || 0), 0);

const percentChange = (current, previous) => {
  if (previous === 0) return current > 0 ? 100 : 0;
  return Number((((current - previous) / previous) * 100).toFixed(1));
};

const ChangeRate = ({ value }) => (
  <span className="featuredMoneyRate">
    {value >= 0 ? "+" : ""}
    {value}%{" "}
    {value >= 0 ? (
      <ArrowUpward className="featuredIcon" />
    ) : (
      <ArrowDownward className="featuredIcon negative" />
    )}
  </span>
);

export default function FeaturedInfo() {
  const { data: orders, isLoading: ordersLoading } = useGetAllOrderQuery();
  const { data: customersData, isLoading: customersLoading } = useGetAllCustomersQuery();
  const { data: lowStockVariants, isLoading: lowStockLoading } = useGetLowStockVariantsQuery();

  if (ordersLoading || customersLoading || lowStockLoading) {
    return (
      <div className="featured">
        {Array.from({ length: 4 }).map((_, i) => (
          <AdminCard key={i} className="featuredItem">
            <AdminSkeleton variant="circle" width={40} height={40} />
            <div style={{ marginTop: 14 }}>
              <AdminSkeleton variant="text" width="60%" />
              <div style={{ marginTop: 10 }}>
                <AdminSkeleton variant="title" width="50%" />
              </div>
            </div>
          </AdminCard>
        ))}
      </div>
    );
  }

  const now = new Date();
  const thisMonth = now.toISOString().slice(0, 7);
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonth = lastMonthDate.toISOString().slice(0, 7);

  const nonCanceled = (orders || []).filter((o) => o.order_Status !== "Canceled");
  const thisMonthOrders = nonCanceled.filter((o) => monthKey(o.order_Placment_Date) === thisMonth);
  const lastMonthOrders = nonCanceled.filter((o) => monthKey(o.order_Placment_Date) === lastMonth);

  const revenueThisMonth = sumRevenue(thisMonthOrders);
  const revenueChange = percentChange(revenueThisMonth, sumRevenue(lastMonthOrders));
  const ordersChange = percentChange(thisMonthOrders.length, lastMonthOrders.length);
  const customerCount = customersData?.count ?? (customersData?.results?.length || 0);
  const lowStockCount = (lowStockVariants || []).length;

  return (
    <div className="featured">
      <AdminCard className="featuredItem">
        <div className="featuredItem__icon featuredItem__icon--success">
          <AccountBalanceWalletOutlined />
        </div>
        <span className="featuredTitle">Revenue (this month)</span>
        <div className="featuredMoneyContainer">
          <span className="featuredMoney">₹{revenueThisMonth.toFixed(2)}</span>
        </div>
        <div className="featuredFooter">
          <ChangeRate value={revenueChange} />
          <span className="featuredSub">vs last month</span>
        </div>
      </AdminCard>
      <AdminCard className="featuredItem">
        <div className="featuredItem__icon featuredItem__icon--info">
          <ShoppingBagOutlined />
        </div>
        <span className="featuredTitle">Orders (this month)</span>
        <div className="featuredMoneyContainer">
          <span className="featuredMoney">{thisMonthOrders.length}</span>
        </div>
        <div className="featuredFooter">
          <ChangeRate value={ordersChange} />
          <span className="featuredSub">vs last month</span>
        </div>
      </AdminCard>
      <AdminCard className="featuredItem">
        <div className="featuredItem__icon featuredItem__icon--purple">
          <PeopleAltOutlined />
        </div>
        <span className="featuredTitle">Total Customers</span>
        <div className="featuredMoneyContainer">
          <span className="featuredMoney">{customerCount}</span>
        </div>
        <div className="featuredFooter">
          <span className="featuredSub">All time</span>
        </div>
      </AdminCard>
      <AdminCard className="featuredItem">
        <div className="featuredItem__icon featuredItem__icon--warning">
          <Inventory2Outlined />
        </div>
        <span className="featuredTitle">Low Stock Items</span>
        <div className="featuredMoneyContainer">
          <span className="featuredMoney">{lowStockCount}</span>
        </div>
        <div className="featuredFooter">
          <span className="featuredSub">Needs restocking</span>
        </div>
      </AdminCard>
    </div>
  );
}
